import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SSEProvider } from './contexts/SSEContext';
import { useAuth } from './hooks/useAuth';
import Layout from './components/layout/Layout';
import ToastContainer from './components/common/Toast';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ApartmentsPage from './pages/ApartmentsPage';
import EventsPage from './pages/EventsPage';
import MyEventsPage from './pages/MyEventsPage';
import MembersPage from './pages/MembersPage';
import CompaniesPage from './pages/CompaniesPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import AuditLogPage from './pages/AuditLogPage';
import StatsPage from './pages/StatsPage';
import './styles/global.css';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const StaffRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (role === 'user') return <Navigate to="/my-events" />;
  return <>{children}</>;
};

const MembersRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ISS-034 — manager 도 회원 관리 조회 허용 (변경 권한은 MembersPage 내부에서 canManage 로 차단).
  // members_view 매트릭스(usePermission.ts)와 반드시 동기화 유지.
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (role === 'user') return <Navigate to="/my-events" />;
  if (role !== 'super_admin' && role !== 'admin' && role !== 'manager') return <Navigate to="/" />;
  return <>{children}</>;
};

const CompaniesRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (role === 'user') return <Navigate to="/my-events" />;
  if (role !== 'super_admin' && role !== 'admin') return <Navigate to="/" />;
  return <>{children}</>;
};

const AuditLogRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ISS-035 — 감사 로그는 super_admin 전용. admin/manager URL 직타 시 대시보드 리다이렉트.
  // audit_log_view 매트릭스(usePermission.ts)와 반드시 동기화.
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (role === 'user') return <Navigate to="/my-events" />;
  if (role !== 'super_admin') return <Navigate to="/" />;
  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, role } = useAuth();
  const defaultPath = role === 'user' ? '/my-events' : '/';

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to={defaultPath} /> : <LoginPage />} />
      <Route path="/signup" element={isAuthenticated ? <Navigate to={defaultPath} /> : <SignupPage />} />
      <Route path="/forgot-password" element={isAuthenticated ? <Navigate to={defaultPath} /> : <ForgotPasswordPage />} />
      <Route path="/my-events" element={<PrivateRoute><Layout><MyEventsPage /></Layout></PrivateRoute>} />
      <Route path="/" element={<StaffRoute><Layout><DashboardPage /></Layout></StaffRoute>} />
      <Route path="/apartments" element={<StaffRoute><Layout><ApartmentsPage /></Layout></StaffRoute>} />
      <Route path="/events" element={<StaffRoute><Layout><EventsPage /></Layout></StaffRoute>} />
      <Route path="/members" element={<MembersRoute><Layout><MembersPage /></Layout></MembersRoute>} />
      <Route path="/companies" element={<CompaniesRoute><Layout><CompaniesPage /></Layout></CompaniesRoute>} />
      <Route path="/stats" element={<StaffRoute><Layout><StatsPage /></Layout></StaffRoute>} />
      <Route path="/announcements" element={<StaffRoute><Layout><AnnouncementsPage /></Layout></StaffRoute>} />
      <Route path="/audit-log" element={<AuditLogRoute><Layout><AuditLogPage /></Layout></AuditLogRoute>} />
      <Route path="*" element={<Navigate to={isAuthenticated ? defaultPath : '/login'} />} />
    </Routes>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <AuthProvider>
      <SSEProvider>
        <AppRoutes />
        <ToastContainer />
      </SSEProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
